<template>
  <h3 class="heading-3">
    <FormattedMessage v-if="postalCode" :definition="translations.shipToLabel">
      <template #postalCode>
        <span>{{ postalCode }}</span>
      </template>
    </FormattedMessage>
    <template v-else>{{ i18n(translations.updateLabel) }}</template>
  </h3>
  <p class="body-2 text-static-default-mid mt-4">
    {{ i18n(translations.modalDescription) }}
  </p>

  <RevForm
    :initial-values
    name="deliveryPostalCode"
    :validate
    @submit="handleSubmit"
  >
    <template #default="{ values, errors, hasError }">
      <div class="mt-24 flex flex-col gap-12 md:flex-row md:items-start">
        <RevInputText
          id="delivery-postal-code-input"
          v-model="values.postalCode"
          autocomplete="postal-code"
          class="w-full md:flex-1"
          :error="errors.postalCode"
          inputmode="numeric"
          :label="i18n(translations.postalCodeLabel)"
        />
        <RevButton
          :disabled="hasError"
          full-width="adaptive"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.modalSubmitCta) }}
        </RevButton>
      </div>
    </template>
  </RevForm>

  <p class="body-2 mt-24">
    <RevLink
      data-qa="delivery-postal-code-switch-country"
      @click="emit('switch-country')"
    >
      {{ i18n(translations.switchCountryLink) }}
    </RevLink>
  </p>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevButton } from '@ds/components/Button'
import { RevForm } from '@ds/components/Form'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'

import { useDeliveryPostalCode } from '~/scopes/shipping/composables/useDeliveryPostalCode/useDeliveryPostalCode'
import {
  type DeliveryPostalCodeFormValues,
  useDeliveryPostalCodeValidate,
} from '~/scopes/shipping/composables/useDeliveryPostalCodeValidate/useDeliveryPostalCodeValidate'

import translations from './PostalCodeForm.translations'

const i18n = useI18n()
const { postalCode, setPostalCode, clearPostalCode } = useDeliveryPostalCode()
const { market } = useMarketplace()
const { validate } = useDeliveryPostalCodeValidate(market.countryCode)

const emit = defineEmits<{
  submit: []
  'switch-country': []
}>()

const initialValues = computed<DeliveryPostalCodeFormValues>(() => ({
  postalCode: postalCode.value ?? '',
  country: market.countryCode,
}))

function handleSubmit(values: DeliveryPostalCodeFormValues) {
  const trimmedPostalCode = values.postalCode.trim()

  if (trimmedPostalCode) {
    setPostalCode(trimmedPostalCode)
  } else {
    clearPostalCode()
  }

  emit('submit')
}
</script>
